/**
 * Gardes de sortie SPA (HA-276).
 *
 * Une navigation SPA (`showApp`, popstate) ne décharge pas la page : `beforeunload` ne voit
 * rien, et une saisie non partie était perdue en silence. Toute navigation SPA passe donc par
 * `guardedNavigate()`, point d'entrée unique : chaque garde « sale » est consulté (vidage de
 * la sauvegarde, sinon confirmation) avant de partir. Sans garde sale, la navigation part
 * SYNCHRONEMENT — aucun changement de cadence pour les pages sans saisie en cours.
 */

export interface LeaveGuard {
    /** Élément propriétaire : retiré du document, le garde est oublié. */
    owner: Element;
    isDirty(): boolean;
    /** Résout `true` si l'on peut partir (saisie partie ou abandon confirmé). */
    settle(): Promise<boolean>;
}

const guards = new Set<LeaveGuard>();
let settling = false;
let unloadConsented = false;

/**
 * La garde SPA a déjà tranché une sortie qui recharge la page (popstate vers l'entrée
 * initiale) : `beforeunload` ne redemande pas.
 */
export function consentUnload(): void { unloadConsented = true; }
export function isUnloadConsented(): boolean { return unloadConsented; }

export function registerLeaveGuard(guard: LeaveGuard): () => void {
    guards.add(guard);
    return () => { guards.delete(guard); };
}

function dirtyGuards(): LeaveGuard[] {
    const dirty: LeaveGuard[] = [];
    guards.forEach((g) => {
        if (!g.owner.isConnected) { guards.delete(g); return; }
        if (g.isDirty()) { dirty.push(g); }
    });
    return dirty;
}

/**
 * Exécute `go` si l'on peut partir, sinon `refused`. Une navigation demandée pendant qu'une
 * précédente attend ses gardes est refusée : la première l'emporte, sans double vidage.
 */
export function guardedNavigate(go: () => void, refused: () => void = () => {}): void {
    if (settling) { refused(); return; }
    const dirty = dirtyGuards();
    if (dirty.length === 0) { go(); return; }
    settling = true;
    (async () => {
        for (const g of dirty) {
            if (!(await g.settle())) { return false; }
        }
        return true;
    })().catch(() => false).then((ok) => {
        settling = false;
        if (ok) { go(); } else { refused(); }
    });
}

/** Tests uniquement : registre vierge. */
export function resetLeaveGuards(): void {
    guards.clear();
    settling = false;
    unloadConsented = false;
}

const IDX = "_hidx";

type HistoryLike = Pick<History, "pushState" | "replaceState" | "go" | "state">;

/**
 * Curseur d'historique : chaque entrée posée par la SPA porte son index, pour qu'un popstate
 * REFUSÉ par un garde ramène l'adresse où l'écran est resté (`history.go(delta)`), sans que ce
 * retour ne redéclenche la garde (popstate suivant ignoré).
 */
export class HistoryCursor {
    private idx: number;
    private suppress = false;
    private current: { state: Map<any, any>; title: string; url: string } | null = null;

    constructor(private readonly history: HistoryLike) {
        const s = history.state;
        this.idx = s instanceof Map && typeof s.get(IDX) === "number" ? s.get(IDX) : 0;
    }

    public record(data: Map<any, any>, title: string, url: string, replace: boolean): void {
        if (!replace) { this.idx += 1; }
        const state = new Map(data);
        state.set(IDX, this.idx);
        if (replace) { this.history.replaceState(state, title, url); }
        else { this.history.pushState(state, title, url); }
        this.current = { state, title, url };
    }

    /**
     * `show` reçoit l'état sans l'index (il ne doit pas partir au serveur). Une entrée SANS
     * état est l'entrée initiale du document (index 0) : rien à montrer côté SPA — accepté,
     * `reload` recharge l'adresse atteinte ; refusé, l'adresse revient comme pour les autres.
     */
    public pop(state: unknown, show: (data: Map<any, any>) => void, reload: () => void): void {
        if (this.suppress) { this.suppress = false; return; }
        if (!(state instanceof Map)) {
            guardedNavigate(() => { this.idx = 0; consentUnload(); reload(); }, () => this.restore(0));
            return;
        }
        const target = typeof state.get(IDX) === "number" ? state.get(IDX) as number : null;
        const data = new Map(state);
        data.delete(IDX);
        guardedNavigate(
            () => { if (target !== null) { this.idx = target; } show(data); },
            () => this.restore(target),
        );
    }

    private restore(target: number | null): void {
        if (target !== null && target !== this.idx) {
            this.suppress = true;
            this.history.go(this.idx - target);
        } else if (this.current) {
            this.history.pushState(this.current.state, this.current.title, this.current.url);
        }
    }
}
